import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  FolderIcon,
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

import { Badge, Button, Card, CardContent, Spinner } from '../../components/ui'
import { useLagAccrualRun } from './api'
import { formatFileSize } from './utils'

function Checklist({ title, items }: { title: string; items: string[] }) {
  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="font-subtitle text-lg text-charcoal dark:text-gray-100">{title}</h2>
        <div className="mt-4 grid gap-3">
          {items.map((item, index) => (
            <div key={item} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
                {index + 1}
              </span>
              <p className="pt-0.5 text-sm leading-6 text-gray-600 dark:text-gray-300">{item}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

export default function RunbookPage() {
  const { data: run, isLoading, error } = useLagAccrualRun()

  if (isLoading) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !run) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-red-600 dark:text-red-400">
          Unable to load runbook.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
      <div className="grid gap-6">
        <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-dark-border dark:bg-dark-surface">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="max-w-3xl">
              <div className="mb-3 flex items-center gap-2">
                <Badge variant="secondary">Source runbook</Badge>
                <Badge variant="secondary">{run.agent_id}</Badge>
              </div>
              <h2 className="font-title text-2xl text-charcoal dark:text-gray-100">
                Close procedure and source package
              </h2>
              <p className="mt-3 text-sm leading-6 text-gray-500 dark:text-gray-400">
                The applet preserves the original EC-02d workbook, report, skill, and load files,
                then exposes the operational pieces as typed API data and controller UI.
              </p>
            </div>
            <Link to="/lag-accruals/source-report">
              <Button type="button" variant="secondary" size="sm">
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                Original HTML
              </Button>
            </Link>
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-3">
          <Checklist title="Execution" items={run.runbook.execution_steps} />
          <Checklist title="HITL Rules" items={run.runbook.hitl_rules} />
          <Checklist title="NetSuite" items={run.runbook.netsuite_requirements} />
        </div>
      </div>

      <aside className="xl:sticky xl:top-6 xl:self-start">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <FolderIcon className="h-5 w-5 text-gray-400" />
              <h2 className="font-subtitle text-lg text-charcoal dark:text-gray-100">
                Included Files
              </h2>
            </div>
            <div className="mt-5 divide-y divide-gray-100 dark:divide-dark-border/70">
              {run.source_files.map(file => (
                <div key={file.name} className="flex items-start justify-between gap-4 py-3">
                  <div className="flex min-w-0 gap-3">
                    {file.name.endsWith('.csv') ? (
                      <ClipboardDocumentCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    ) : (
                      <DocumentTextIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    )}
                    <p className="min-w-0 break-words text-sm text-charcoal dark:text-gray-100">
                      {file.name}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                    {formatFileSize(file.size_bytes)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </aside>
    </div>
  )
}
